import * as React from 'react';
import { Card } from "../components/Card";
import { Input } from "../components/Input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { preloadPhoneValidator, stepOneSchema, StepOneType } from "../schemas/formSchema";
import { useInputFields } from "../contexts/InputFields";
import { useNavigate, useLocation } from "react-router-dom";
import { PhoneInputField } from "../components/PhoneInputField";
import { DiscountTicket } from "../components/DiscountTicket";
import { Button } from "../components/Button";
import { usePlans } from "../contexts/Plans";
import {fireAlert} from "../services/alert";
import { CompanyApi } from "../api/company";
import { TurnstileWidget, TurnstileWidgetRef } from "../components/TurnstileWidget";

type Props = {};

export const StepOne = (props: Props) => {
    const { formData, onSubmit, onError } = useInputFields();
    const captchaRef = React.useRef<TurnstileWidgetRef>(null);
    const { sendLeads } = CompanyApi();
    const { selectedPlan, loadingTicket, discountTicket, reativa } = usePlans();
    const [loading, setLoading] = React.useState(false);
    const [captchaLoading, setCaptchaLoading] = React.useState(true);
    const [captchaError, setCaptchaError] = React.useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<StepOneType>({
        resolver: zodResolver(stepOneSchema),
        defaultValues: {
            fullName: formData.fullName || '',
            email: formData.email || '',
            politicas: true,
        },
    });


    const handleFormSubmit = async (data: StepOneType) => {
        if (!selectedPlan) {
            fireAlert({
                icon: 'error',
                title: 'Erro!',
                text: 'Selecione um plano para continuar.',
                confirmButtonText: 'OK'
            })
            return;
        }

        // Extract UTM and tracking parameters from URL
        const urlParams = new URLSearchParams(location.search);
        const trackingParams = {
            utm_content: urlParams.get('utm_content'),
            utm_medium: urlParams.get('utm_medium'),
            utm_campaign: urlParams.get('utm_campaign'),
            utm_source: urlParams.get('utm_source'),
            utm_term: urlParams.get('utm_term'),
            utm_referrer: urlParams.get('utm_referrer'),
            revendedor: urlParams.get('revendedor'),
            referrer: urlParams.get('referrer'),
            gclientid: urlParams.get('gclientid'),
            gclid: urlParams.get('gclid'),
            fbclid: urlParams.get('fbclid'),
        };

        setLoading(true);
        // Baixa o chunk do passo 2 em paralelo ao envio do lead
        import('./StepTwo');
        try {
            await sendLeads(data, selectedPlan, discountTicket, trackingParams);
        } catch (error: any) {
            if (error.status === 429) {
                fireAlert({
                    icon: 'error',
                    title: 'Erro de captcha!',
                    text: 'Aguarde um momente e tente novamente!',
                    confirmButtonText: 'OK'
                })
                setLoading(false)
                return;
            }
            if (error.status === 409) {
                fireAlert({
                    icon: 'error',
                    title: 'E-mail já cadastrado!',
                    text: 'Esse e-mail já está cadastrado, tente outro.',
                    confirmButtonText: 'OK'
                })
                setLoading(false)
                return;
            }

            fireAlert({
                icon: 'error',
                title: 'Erro inesperado',
                text: 'Aguarde um momento e tente novamente!',
                confirmButtonText: 'OK'
            })
            return;
        } finally {
            setLoading(false)
            captchaRef.current?.reset();
        }

        //save in local storage
        localStorage.setItem('formData', JSON.stringify(data));
        onSubmit(data);

        // Preserve all existing query parameters when navigating
        navigate({
            pathname: '/passo-2',
            search: location.search
        });
    };

    return (
        <Card>
            <form
                onSubmit={handleSubmit(handleFormSubmit, onError)} noValidate
                onFocusCapture={preloadPhoneValidator}
                name='step-one-form'>
                <div className="flex flex-col gap-6 w-full">
                    <div>
                        <h1 className="title">{reativa ? 'Contratar agora' : 'Iniciar teste grátis'}</h1>
                        <h2 className="text-14 leading-tight mt-1 mr-8">
                            Profissionalize suas vendas através do catálogo online do Conecta Venda preenchedo o formulário abaixo:
                        </h2>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Input
                            label="Nome completo"
                            placeholder="Nome completo"
                            {...register("fullName")}
                        />
                        <PhoneInputField
                            label="Celular | WhatsApp"
                            placeholder="(00) 00000-0000"
                            {...register("phone")}
                        />
                        <Input
                            label="E-mail"
                            placeholder="email@email.com.br"
                            type="email"
                            {...register("email")}
                        />
                        <Input
                            label="Senha"
                            placeholder="******"
                            type="password"
                            {...register("password")}
                            sensitive={true}
                        />

                        <div className="separator-line mt-4" />
                        <DiscountTicket />
                    </div>
                    <div className="w-full flex justify-start">
                        <div className="text-tertiary text-12 leading-4">
                            Ao clicar em Avançar, você confirma que você tem pelo menos 18 anos, que concorda que leu nossa {' '}
                            <a
                                href="https://assine.conectavenda.com.br/politica-de-privacidade/"
                                target="_blank"
                                className="underline"
                            >
                                Política de Privacidade
                            </a>
                            {errors.politicas && (
                                <span className="text-red-500 text-11 mt-1 block">
                                    {errors.politicas.message}
                                </span>
                            )}
                        </div>
                    </div>
                    <TurnstileWidget
                        ref={captchaRef}
                        fieldName="captchaStepOne"
                        setValue={setValue}
                        register={register}
                        onLoadingChange={setCaptchaLoading}
                        onErrorChange={setCaptchaError}
                    />
                    <Button
                        type="submit"
                        className="btn"
                        loading={loading}
                        disabled={captchaLoading || !!captchaError || loading}
                    >
                        Avançar
                    </Button>
                </div>
            </form>
        </Card>
    );
};
